import IFallbackService from '../../../services/fallbackService/IFallbackService';
import * as T from './types';
import { getServices } from '../../../infra/startup';
import bindAllMethods from '../../../utils/bindAllMethods';

/**
 * Fallback Interface V1
 * Delegates to FallbackService for all operations
 */
export default class FallbackInterface implements T.FallbackInterfaceType {
  private _fallbackService: IFallbackService;

  constructor() {
    const services = getServices();
    this._fallbackService = services.fallbackService;
    bindAllMethods(this);
  }

  get defaultFallback(): T.FallbackStrategyObjectType {
    return this._fallbackService.defaultFallback;
  }

  get eventName(): string {
    return this._fallbackService.eventName;
  }

  getFallbackByKey(key: string): T.FallbackLoadAssetStrategyType | undefined {
    return this._fallbackService.getFallbackByKey(key);
  }

  getDefaultRootFallback(): T.FallbackRedirectStrategyType {
    return this._fallbackService.getDefaultRootFallback();
  }

  getFallbackByPath(path: string): T.FallbackLoadAssetStrategyType | undefined {
    return this._fallbackService.getFallbackByPath(path);
  }

  getCurrentFallback(): T.FallbackStateType {
    return this._fallbackService.getCurrentFallback();
  }

  listen(callback: (state: T.FallbackStateType) => void): () => void {
    return this._fallbackService.listen(callback);
  }

  useReactHook(React: any): T.FallbackLoadAssetStrategyType {
    return this._fallbackService.useReactHook(React);
  }

  clearFallbackState(): void {
    this._fallbackService.clearFallbackState();
  }

  setCurrentFallback(
    fallback?: T.FallbackStrategyObjectType,
    options?: T.SetCurrentFallbackOptionsType
  ): void {
    this._fallbackService.setCurrentFallback(fallback, options);
  }

  setCurrentFallbackByKey(
    key: string,
    options?: T.SetCurrentFallbackOptionsType
  ): void {
    this._fallbackService.setCurrentFallbackByKey(key, options);
  }

  /**
   * Get the interface methods for external use
   */
  getInterface(): T.FallbackInterfaceType {
    return {
      defaultFallback: this.defaultFallback,
      eventName: this.eventName,
      getFallbackByKey: this.getFallbackByKey.bind(this),
      getDefaultRootFallback: this.getDefaultRootFallback.bind(this),
      getFallbackByPath: this.getFallbackByPath.bind(this),
      getCurrentFallback: this.getCurrentFallback.bind(this),
      listen: this.listen.bind(this),
      useReactHook: this.useReactHook.bind(this),
      clearFallbackState: this.clearFallbackState.bind(this),
      setCurrentFallback: this.setCurrentFallback.bind(this),
      setCurrentFallbackByKey: this.setCurrentFallbackByKey.bind(this)
    };
  }
}
